import React from 'react'
import classNames from 'classnames'
import { mq } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import kebabCase from 'lodash.kebabcase'
import { Logo } from 'marketing-site/src/library/elements/Logo'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { Text } from 'marketing-site/src/library/elements/Text'

export const LogoCarousel = ({
  logos,
  backgroundColor = 'transparent',
}: IProps): React.ReactElement => {
  const classes = classNames('logo-carousel', 'grid-layout', {
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })
  const itemsClasses = classNames('logo-carousel__items', {
    'logo-carousel__items--animate': true,
  })
  const itemsDuplicateClasses = classNames(
    'logo-carousel__items',
    'logo-carousel__items-duplicate',
    {
      'logo-carousel__items--animate': true,
    },
  )
  const { translate } = useTranslate()

  return (
    <div className={classes} data-testid="logo-carousel">
      <div className="logo-carousel__inner">
        <div className="logo-carousel__eyebrow">
          <Text size="eyebrow">
            {translate('Trusted by +25,000 businesses'.toLocaleUpperCase())}
          </Text>
        </div>
        <div className="logo-carousel__logo-carousel">
          <div className={itemsClasses}>
            {logos.map((logo) => (
              <div className="logo-party__logo" key={logo.logoUrl}>
                <Logo {...logo} />
              </div>
            ))}
          </div>
          <div className={itemsDuplicateClasses}>
            {logos.map((logo) => (
              <div className="logo-party__logo" key={logo.logoUrl}>
                <Logo {...logo} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>
        {`
          .logo-carousel {
            width: 100%;
            display: flex;
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
            position: relative;
            overflow: hidden;
            scrollbar-width: none;

            &__inner {
              max-width: 90rem;
              position: relative;
            }

            &__logo-carousel {
              grid-column: 1/-1;
              display: flex;
              width: 100%;
            }

            &__eyebrow {
              margin-top: 2.5rem;
              grid-column: 1/-1;
              color: white;
              font-weight: 700;
              text-align: center;
            }

            &__items {
              display: flex;
              gap: 0.75rem;
              padding: 1.25rem;
              position: relative;

              :global(.wired-card) {
                flex: 0 0 100%;
              }

              .logo-party__logo {
                &:nth-child(1) {
                  padding-left: 1rem;
                }
              }
            }
          }

          @media (${mq.mobile}) {
            .logo-carousel {
              &__items {
                gap: 1.75rem;
                padding-left: 0;
                padding-right: 0;

                &--animate {
                  animation: ${logos.length * 3}s linear scrolling infinite;
                }

                .logo-party__logo {
                  justify-content: center;
                  display: block;
                  margin: 0 0.25rem;
                }
              }
            }

            @keyframes scrolling {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          }

          @media (${mq.tablet}) {
            .logo-carousel {
              padding-top: 2.5rem;
              padding-bottom: 2.5rem;

              &__eyebrow {
                margin-top: 0;
              }

              &__items {
                gap: 1.75rem;
                padding-left: 0;
                padding-right: 0;

                &--animate {
                  animation: ${logos.length * 5}s linear scrolling infinite;
                }

                .logo-party__logo {
                  justify-content: center;
                  display: block;
                  margin: 0 1rem;
                }
              }
            }

            @keyframes scrolling {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          }

          @media (${mq.desktop}) {
            .logo-carousel {
              padding-top: 3rem;
              padding-bottom: 3rem;

              &__items {
                gap: 2rem;
              }
            }

            @keyframes scrolling {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          }

          @media (${mq.desktopLg}) {
            .logo-carousel {
              &__inner {
                mask-image: linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0) 0%,
                  white calc(0% + 2rem),
                  white calc(100% - 2rem),
                  rgba(255, 255, 255, 0) 100%
                );
              }
            }
          }
        `}
      </style>
    </div>
  )
}
